import React, { useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import DateField from 'components/Form/dateField';
import { formatDateHandler } from 'utils/common';
import { useUniformMgmt } from '../context/uniformMgmtContext';
import { employeeGenderFilterOptions, uniformGenderOptions } from '../constants';
import AppliedFilterSummary, { summariseSelection } from '../components/AppliedFilterSummary';

/** The server's per-field id cap, mirrored here only to warn before the
 *  request is sent — the server still enforces it, and its 400 is surfaced
 *  verbatim by `uniformErrorMessage`, so this is a courtesy rather than the
 *  check.
 *
 *  Was `MAX_POSITION_FILTER_IDS` when the stock report's position picker was
 *  the only multi in the bar. It now caps cost centre, employee, position and
 *  uniform item alike (the same 200 the issuance endpoints enforce), so a name
 *  that says "position" would be actively misleading about which fields it
 *  guards. */
const MAX_FILTER_IDS = 200;

/** Every multi-select filter takes `?field=` as a single id OR a comma-separated
 *  list. axios' default serializer turns an array into `position[]=1&position[]=2`,
 *  which the server does not read, so a multi-select has to be stored already
 *  joined. Keeping the CSV string (rather than an array plus a join at request
 *  time) also keeps `cleanParams`, `hasAnyValue` and the Excel export working
 *  unchanged: an empty selection becomes `undefined` and disappears, whereas a
 *  stored `[]` is truthy and would pin the "Clear" button on forever. */
const csvToValues = (csv) => (csv === undefined || csv === null || csv === '')
  ? []
  : String(csv).split(',').map((s) => s.trim()).filter(Boolean);

const findOptions = (options, csv) => {
  const wanted = csvToValues(csv);
  if (!wanted.length) return [];
  // String-compared: option values are numeric ids but the CSV round-trip
  // makes them strings.
  return (options || []).filter((o) => wanted.includes(String(o.value)));
};

/** Strips undefined/null/"" values so callers can spread a filter-bar value
 *  straight into query params without shipping empty-string filters. */
export const cleanParams = (value = {}) => {
  const params = {};
  Object.entries(value).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') params[key] = val;
  });
  return params;
};

const findOption = (options, val) => {
  if (val === undefined || val === null || val === '') return null;
  return (options || []).find((o) => o.value === val) || null;
};

const isSet = (val) => val !== undefined && val !== null && val !== '';

const fieldWrapStyle = { minWidth: 190 };
/** Wider than a single-select: a multi holds chips, and at 190px even two
 *  short position names collapse straight to "+N more". Capped so it cannot
 *  push the rest of the bar onto its own line on a laptop. */
const multiFieldWrapStyle = { minWidth: 240, maxWidth: 340 };
const labelStyle = { fontSize: 12 };

const rowClass = 'd-flex flex-wrap gap-3 align-items-end';

/** Rides on `.btn .btn-link` — the same base the Clear button in this bar
 *  already uses — purely to inherit Bootstrap's focus ring, since nothing in
 *  the committed theme gives a bare <button> a visible one. Everything that
 *  would make it read as an action (link blue, underline, button padding) is
 *  overridden back out: this is a disclosure, and the Apply/Clear pair on the
 *  same line must keep the only button weight on the row. */
const disclosureStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--brand-text)',
  textDecoration: 'none',
  // Aligns the control with the field labels beside it, which sit on
  // `align-items-end` with an input's worth of height below them.
  marginBottom: 8,
};

const badgeStyle = {
  display: 'inline-block',
  minWidth: 18,
  padding: '0 6px',
  marginLeft: 6,
  borderRadius: 9,
  background: '#D3001A',
  color: '#FFFFFF',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: '18px',
  textAlign: 'center',
};

const morePanelStyle = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px solid #F1F2F4',
};

/**
 * Shared, presentational filter bar for the reports dashboard tabs.
 *
 * Renders only the fields requested via `fields`, sourcing their option
 * lists from redux (CASA/position/employee) and the uniform mgmt context
 * (sizes/warehouses); uniform items are loaded once by the dashboard and
 * passed down via `itemOptions` rather than re-fetched per tab, and the
 * `groupBy` options via `groupByOptions` because each report rolls up along
 * its own dimensions.
 *
 * `value`/`onChange` operate on the DRAFT filter object (flat query-param
 * shape, e.g. `{ date_from, cost_center, uniform_item, ... }`) — picking a
 * value never fires a request by itself. Only `onApply` (owned by the
 * calling tab) should commit the draft into the filters actually queried.
 *
 * ## Layout
 *
 * `primaryFields` splits the bar in two: the handful of filters a user of
 * that report reaches for first stay on one always-visible row, and the rest
 * move behind a "More filters" disclosure. Without it every field renders on
 * a single `flex-wrap` row, which is fine at three or four fields and is why
 * the employee-history tab passes nothing — but the issuance tab's nine
 * fields (ten controls) wrapped into two or three ragged lines with no
 * grouping, which is the layout this prop exists to fix.
 *
 * A hidden filter is never silent: the disclosure carries a count badge and,
 * while collapsed, the bar lists every hidden filter that currently holds a
 * value underneath. Otherwise a filter left set behind a closed panel reads
 * as missing rows with no visible cause — the one confusion a disclosure can
 * introduce that the ragged row could not.
 */
const ReportFilterBar = ({
  fields = [],
  primaryFields,
  value = {},
  appliedValue,
  onChange,
  statusOptions = [],
  itemOptions = [],
  itemOptionsLoading = false,
  /** Options for the `groupBy` field, supplied by the tab because each
   *  report rolls up along different dimensions. The cleared state is the
   *  report's default grain, named by `groupByPlaceholder` so the control
   *  says what "no grouping" means here rather than "Filter by Group by". */
  groupByOptions = [],
  groupByPlaceholder = 'No grouping',
  /** Tab-specific single selects that do not deserve a spec of their own:
   *  `[{ field, key, label, options, placeholder }]`. `field` is the name
   *  to list in `fields`; `key` the query param it writes. */
  extraSelects = [],
  searchPlaceholder = 'Search...',
  onApply,
  onClear,
}) => {
  const { costCenter, costCenterLoading, userData, userDataLoading } = useSelector((state) => state.common);
  const { sizeOptions, sizesLoading, warehouseOptions, warehousesLoading,
    uniformPositionOptions, uniformPositionsLoading } = useUniformMgmt();

  const [moreOpen, setMoreOpen] = useState(false);
  const instanceId = useId();
  const morePanelId = `${instanceId}-more-filters`;

  // `code` is not rendered — the label stays the name — but FilterSelect's
  // search matches every string field on an option, so typing a store code
  // finds its cost centre. Feeds the Cost Center filter on every report tab.
  const casaOptions = (costCenter || []).map((c) => ({ label: c.name || c.code, value: c.id, code: c.code || '' }));
  // Positions a uniform is mapped to, not every position in the company:
  // the rest return an empty grid when picked. Masters keeps the full list.
  const positionOptions = uniformPositionOptions;
  // Employee gender uses employeeGenderFilterOptions (CompanyUser.gender values)
  // — never uniformGenderOptions, which is the item M/F/U vocabulary and would
  // silently match zero employees if sent as ?gender=.
  const employeeOptions = (userData || [])
    .slice()
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
    .map((u) => ({
      label: `${u.first_name} ${u.last_name}${u.emp_no ? ` (${u.emp_no})` : ''}`,
      value: u.id, emp_no: u.emp_no || '',
    }));

  const setField = (key, val) => onChange({ ...value, [key]: val });
  const setFromOption = (key) => (option) => setField(key, option ? option.value : undefined);

  const hasAnyValue = Object.values(value).some(isSet);

  /**
   * One descriptor per supported field name.
   *
   * Single source of truth for a field's query-param key(s), its label and
   * its option list, so the renderer below and the hidden-filter summary
   * cannot drift apart — the summary has to name a field and resolve its
   * value to a human label, and duplicating either in a second switch is how
   * "Position" ends up captioned two different ways on two rows of the same
   * bar.
   *
   * `kind` picks the renderer; `keys` is derived from `key` for every field
   * except the date range, which owns two params.
   */
  const fieldSpecs = {
    dateRange: { kind: 'dateRange', label: 'Date', keys: ['date_from', 'date_to'] },
    // The four "who / what / where" filters are plural specs storing a CSV,
    // because every endpoint behind them now reads their param as a list.
    // Plural spec name = `kind: 'multi'` = a CSV string in `value`; that
    // convention started with `positions` and is what tells a reader which
    // shape a field holds without opening the renderer.
    casas: {
      kind: 'multi', key: 'cost_center', label: 'Cost Center', options: casaOptions,
      loading: costCenterLoading, max: MAX_FILTER_IDS, noOptionsLabel: 'cost centers',
    },
    employees: {
      kind: 'multi', key: 'company_user', label: 'Employee', options: employeeOptions,
      loading: userDataLoading, max: MAX_FILTER_IDS, noOptionsLabel: 'employees',
    },
    positions: {
      kind: 'multi', key: 'position', label: 'Position', options: positionOptions,
      loading: uniformPositionsLoading, max: MAX_FILTER_IDS, noOptionsLabel: 'positions',
    },
    // Employee gender (CompanyUser.gender, male/female/other), NOT the item
    // M/F/U vocabulary `itemGender` sends — the two share the `gender` param
    // but never a spec. Sending male/female to the stock report is a 400, and
    // sending M/F to the issuance report silently matches zero employees.
    gender: { kind: 'select', key: 'gender', label: 'Gender', options: employeeGenderFilterOptions, loading: false },
    itemGender: { kind: 'select', key: 'gender', label: 'Item Gender', options: uniformGenderOptions, loading: false },
    combined: { kind: 'combined', key: 'combined', label: 'Warehouses' },
    // A display MODE like `combined`, not a filter: it changes the row grain
    // and column set, so tabs list it in `primaryFields` — a mode must never
    // sit behind a disclosure the user closes and forgets.
    groupBy: {
      kind: 'select', key: 'group_by', label: 'Group by', options: groupByOptions,
      loading: false, placeholder: groupByPlaceholder,
    },
    // `item` (single) is still what the PO, transfer and stock reports ask
    // for; only the issuance-side reports send a list today, so both specs
    // exist and write the same `uniform_item` param.
    item: { kind: 'select', key: 'uniform_item', label: 'Uniform Item', options: itemOptions, loading: itemOptionsLoading },
    items: {
      kind: 'multi', key: 'uniform_item', label: 'Uniform Item', options: itemOptions,
      loading: itemOptionsLoading, max: MAX_FILTER_IDS, noOptionsLabel: 'uniform items',
    },
    size: { kind: 'select', key: 'size', label: 'Size', options: sizeOptions, loading: sizesLoading },
    warehouse: { kind: 'select', key: 'warehouse', label: 'Warehouse', options: warehouseOptions, loading: warehousesLoading },
    sourceWarehouse: { kind: 'select', key: 'source_warehouse', label: 'Source Warehouse', options: warehouseOptions, loading: warehousesLoading },
    destinationWarehouse: { kind: 'select', key: 'destination_warehouse', label: 'Destination Warehouse', options: warehouseOptions, loading: warehousesLoading },
    status: { kind: 'select', key: 'status', label: 'Status', options: statusOptions, loading: false },
    supplier: { kind: 'text', key: 'supplier', label: 'Supplier', placeholder: 'Filter by supplier' },
    search: { kind: 'text', key: 'search', label: 'Search', placeholder: searchPlaceholder, minWidth: 220 },
  };

  extraSelects.forEach((extra) => {
    fieldSpecs[extra.field] = {
      kind: 'select', key: extra.key, label: extra.label, options: extra.options || [],
      loading: false, placeholder: extra.placeholder,
    };
  });

  const specKeys = (spec) => spec.keys || [spec.key];

  const selectField = (key, label, options, isLoading, placeholder) => (
    <div style={fieldWrapStyle} key={key}>
      <label className="col-form-label d-block" style={labelStyle}>{label}</label>
      <FilterSelect isClearable onChange={setFromOption(key)} optionLabel="label" optionValue="value"
        options={options} value={findOption(options, value[key])} placeholder={placeholder || `Filter by ${label}`}
        isLoading={isLoading} />
    </div>
  );

  /** Multi variant of `selectField`. Ticking options never fires a request, so
   *  the menu stays open on select (`checkboxMenu`) — the whole point of a
   *  multi here is picking several in one go.
   *
   *  `selectAll` ticks what the current search leaves visible, not the whole
   *  list, which is what makes it usable on the employee picker: "type
   *  'kitchen', select all 9" rather than "select all 400". */
  const multiSelectField = (key, label, options, isLoading, { max, noOptionsLabel } = {}) => {
    const selected = findOptions(options, value[key]);
    const overMax = max && selected.length > max;
    return (
      <div style={multiFieldWrapStyle} key={key}>
        <label className="col-form-label d-block" style={labelStyle}>{label}</label>
        <FilterSelect isMulti isClearable checkboxMenu selectAll maxVisibleValues={3}
          onChange={(next) => setField(key, (next || []).map((o) => o.value).join(',') || undefined)}
          optionLabel="label" optionValue="value" options={options} value={selected}
          placeholder={`Filter by ${label}`} isLoading={isLoading}
          noOptionsLabel={noOptionsLabel || label.toLowerCase()} />
        {overMax && (
          // Said before Apply rather than only after the server's 400, because
          // the user cannot see how many they have ticked once the chips are
          // capped at "+N more".
          <div className="text-danger" style={{ fontSize: 11, marginTop: 2 }}>
            {/* The visible text carries the live count; it is hidden from
                assistive tech because it changes on every further tick, and a
                live region re-announces on every change. */}
            <span aria-hidden="true">{selected.length} selected — the report accepts at most {max}.</span>
            {/* What is announced instead: one sentence with no changing part,
                so `role="status"` (polite, never interrupting) fires once when
                the cap is first crossed and stays silent while ticking
                continues past it.
                The field is named from `label` rather than hardcoded: this
                branch used to say "Too many positions selected" for every
                field, which was true only while the stock report's position
                picker was the bar's only multi. Four fields can breach it now,
                and a screen-reader user told the wrong one has no way to see
                which picker is actually over. */}
            <span className="visually-hidden" role="status">
              Too many {label.toLowerCase()} values selected. The report accepts at most {max}.
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderField = (field) => {
    const spec = fieldSpecs[field];
    if (!spec) return null;
    switch (spec.kind) {
      case 'dateRange':
        return (
          <React.Fragment key="dateRange">
            <div style={{ minWidth: 170 }}>
              {/* Ids are scoped to this bar's `useId`, not the fixed
                  "report-date-from" they used to be. The reports dashboard is
                  an antd <Tabs>, which keeps every pane the user has visited
                  mounted — so after two tabs there were two #report-date-from
                  inputs in the document and each label's `htmlFor` resolved to
                  whichever came first, i.e. the other tab's. */}
              <label className="col-form-label d-block" style={labelStyle} htmlFor={`${instanceId}-date-from`}>Date From</label>
              {/* `cleanParams` drops '' before the request is built, so clearing
                  either end still removes the filter rather than sending a blank. */}
              <DateField id={`${instanceId}-date-from`} value={value.date_from} placeholder="From"
                max={value.date_to} onChange={(next) => setField('date_from', next)} />
            </div>
            <div style={{ minWidth: 170 }}>
              <label className="col-form-label d-block" style={labelStyle} htmlFor={`${instanceId}-date-to`}>Date To</label>
              {/* Mirrors the "from" bound so the two ends cannot be crossed; the
                  native inputs enforced nothing here and would happily submit an
                  inverted range that returns zero rows with no explanation. */}
              <DateField id={`${instanceId}-date-to`} value={value.date_to} placeholder="To"
                min={value.date_from} onChange={(next) => setField('date_to', next)} />
            </div>
          </React.Fragment>
        );
      case 'combined':
        return (
          <div key="combined" className="d-flex flex-column" style={{ minWidth: 190 }}>
            {/* A visual caption only, so this column's heading lines up with
                the real field labels beside it — NOT a second <label> for the
                switch. It used to carry `htmlFor="report-combined"` as well,
                which gave the input two labels and an accessible name of
                "Warehouses Combined (all warehouses)". The switch's own label
                below already names it completely, so this is hidden from
                assistive tech rather than being reworded. */}
            <span className="col-form-label d-block" style={labelStyle} aria-hidden="true">
              {spec.label}
            </span>
            <div className="form-check form-switch mt-1">
              {/* Stored as the string '1' because that is literally what the
                  server tests for (`combined in ("1","true","True")`), and it
                  survives `cleanParams` — a boolean `false` would be kept and
                  sent as `combined=false`, which reads as "off" to a human but
                  is simply not one of the accepted on-values. `undefined` is
                  the honest off state: the param is absent. */}
              <input className="form-check-input" type="checkbox" role="switch" id={`${instanceId}-combined`}
                checked={value.combined === '1'}
                onChange={(e) => setField('combined', e.target.checked ? '1' : undefined)} />
              <label className="form-check-label" htmlFor={`${instanceId}-combined`}
                style={{ fontSize: 13, cursor: 'pointer' }}>
                Combined (all warehouses)
              </label>
            </div>
          </div>
        );
      case 'multi':
        return multiSelectField(spec.key, spec.label, spec.options, spec.loading, {
          max: spec.max, noOptionsLabel: spec.noOptionsLabel,
        });
      case 'text':
        return (
          <div style={{ minWidth: spec.minWidth || 190 }} key={field}>
            <label className="col-form-label d-block" style={labelStyle}>{spec.label}</label>
            <input type="text" className="form-control" placeholder={spec.placeholder}
              value={value[spec.key] || ''} onChange={(e) => setField(spec.key, e.target.value)} />
          </div>
        );
      case 'select':
      default:
        return selectField(spec.key, spec.label, spec.options, spec.loading, spec.placeholder);
    }
  };

  /**
   * Human-readable "Label: value" for a field holding a value in `source`, or
   * null when it is empty. Resolves ids to option labels rather than printing
   * raw query values.
   *
   * `source` is the APPLIED filters, not the draft — see the summary row at
   * the bottom of this component for why that distinction is the whole point.
   */
  const summariseField = (field, source) => {
    const spec = fieldSpecs[field];
    if (!spec) return null;
    switch (spec.kind) {
      case 'dateRange': {
        const from = isSet(source.date_from) ? formatDateHandler(source.date_from) : null;
        const to = isSet(source.date_to) ? formatDateHandler(source.date_to) : null;
        if (from && to) return `Date: ${from} – ${to}`;
        if (from) return `Date: from ${from}`;
        if (to) return `Date: up to ${to}`;
        return null;
      }
      case 'combined':
        return source.combined === '1' ? 'Warehouses: combined' : null;
      case 'multi': {
        const raw = source[spec.key];
        if (!isSet(raw)) return null;
        const selected = findOptions(spec.options, raw);
        // Same fallback as `select`, and for the same reason: option lists
        // load asynchronously, and an applied filter must never drop out of
        // the summary while its labels are still in flight — that is exactly
        // the "badge says 1, summary says nothing" hole this row exists to
        // close. The CSV still yields a true count with no options at all.
        if (!selected.length) return `${spec.label}: ${csvToValues(raw).length} selected`;
        return summariseSelection(spec.label, selected);
      }
      case 'text':
        return isSet(source[spec.key]) ? `${spec.label}: ${source[spec.key]}` : null;
      case 'select':
      default: {
        if (!isSet(source[spec.key])) return null;
        // Falls back to the raw value rather than hiding the entry: an option
        // list that has not finished loading must not make an active filter
        // disappear from the summary.
        const option = findOption(spec.options, source[spec.key]);
        return `${spec.label}: ${option ? option.label : source[spec.key]}`;
      }
    }
  };

  /**
   * Split `fields` into the always-visible row and the disclosure.
   *
   * Primary order follows `primaryFields` (the tab's stated priority), not the
   * declaration order in `fields`; everything else keeps its original order.
   * With no `primaryFields`, or when nothing would be left to hide, the whole
   * set renders flat and no disclosure is drawn — a three-field bar must not
   * grow a "More filters" button with an empty panel behind it.
   */
  const knownFields = fields.filter((f) => fieldSpecs[f]);
  const wantedPrimary = (primaryFields || []).filter((f) => knownFields.includes(f));
  const remainder = knownFields.filter((f) => !wantedPrimary.includes(f));
  const splitBar = wantedPrimary.length > 0 && remainder.length > 0;
  const primary = splitBar ? wantedPrimary : knownFields;
  const more = splitBar ? remainder : [];

  /**
   * The badge counts the DRAFT; the summary line below reads the APPLIED
   * filters. That split is deliberate, and each half is true on its own terms
   * in all four combinations:
   *
   *   - the badge answers "is there anything set in the panel I just closed",
   *     which is a question about the controls, so it reads the controls;
   *   - the summary answers "why is the table missing rows", which is a
   *     question about the query, so it reads the query.
   *
   * Reading the draft for both would let the summary claim the table is
   * filtered by something the user staged and never applied; reading the
   * applied filters for both would leave a picked-but-unapplied value with no
   * indication at all once the panel is shut. `appliedValue` is optional and
   * falls back to the draft, which is exact for any caller that applies
   * immediately.
   */
  const summarySource = appliedValue || value;
  const hiddenSummaries = more
    .map((field) => ({ field, text: summariseField(field, summarySource) }))
    .filter((entry) => entry.text);
  // Counted off the same specs the panel renders, so a hidden field can never
  // go uncounted. `keys` covers the date range's two params without
  // double-counting a range as two filters.
  const hiddenActiveCount = more.filter((f) => specKeys(fieldSpecs[f]).some((k) => isSet(value[k]))).length;

  return (
    <div className="p-3 mb-3" style={{ background: '#fff', border: '1px solid #F1F2F4', borderRadius: 12 }}>
      <div className={rowClass}>
        {primary.map((field) => renderField(field))}
        {more.length > 0 && (
          <button type="button" style={disclosureStyle} className="btn btn-link p-0"
            aria-expanded={moreOpen} aria-controls={morePanelId}
            onClick={() => setMoreOpen((open) => !open)}>
            <i className={`fa fa-chevron-${moreOpen ? 'up' : 'down'} me-2`} aria-hidden="true"
              style={{ fontSize: 10 }} />
            More filters
            {hiddenActiveCount > 0 && (
              <>
                <span style={badgeStyle} aria-hidden="true">{hiddenActiveCount}</span>
                {/* The badge is decorative; the count belongs in the button's
                    accessible name so it is announced with the control rather
                    than as a stray number after it. */}
                <span className="visually-hidden">
                  , {hiddenActiveCount} {hiddenActiveCount === 1 ? 'filter' : 'filters'} set
                </span>
              </>
            )}
          </button>
        )}
        <div className="d-flex gap-2 ms-auto">
          {hasAnyValue && (
            <Button label="Clear" type="button" className="btn btn-link p-0"
              style={{ color: 'var(--brand-text-muted)', fontWeight: 600 }} onClick={onClear} />
          )}
          <Button label="Apply" type="button" className="btn btn-primary btn-sm" onClick={onApply} />
        </div>
      </div>

      {more.length > 0 && (
        // Rendered even while collapsed, with `hidden`, rather than unmounted:
        // `aria-controls` above has to point at an element that exists, and
        // `hidden` keeps the collapsed panel out of the tab order without
        // discarding what the user had already picked in it.
        <div id={morePanelId} hidden={!moreOpen} style={morePanelStyle}>
          <div className={rowClass}>
            {more.map((field) => renderField(field))}
          </div>
        </div>
      )}

      {/* The whole reason a disclosure is safe here: a filter the table is
          ACTUALLY filtered by, sitting behind a closed panel, is still named
          on screen — so missing rows always have a visible cause. Once the
          panel is open the controls say it themselves, hence the `!moreOpen`.
          Shared with the issuance list's drawer, which has the same problem
          with every field hidden instead of some. */}
      {!moreOpen && (
        <AppliedFilterSummary
          lead="Also filtering by:"
          entries={hiddenSummaries.map((entry) => entry.text)}
        />
      )}
    </div>
  );
};

export default ReportFilterBar;
